/// <amd-module name="TFS/Views/ProjectOverviewPage/Components/ProjectOverviewPage" />
import { IFavoritesService } from "Favorites/Common/FavoritesService";
import { Fabric } from "OfficeFabric/Fabric";
import { Overlay } from "OfficeFabric/Overlay";
import * as React from "react";
import { ITfsPageService } from "Tfs/Platform/Page";
import { ProjectOverviewPageActionCreator } from "TFS/Views/ProjectOverviewPage/Actions/ProjectOverviewPageActionCreator";
import { AddUserDialogProps, ProjectOverviewPageHeader } from "TFS/Views/ProjectOverviewPage/Components/ProjectOverviewPageHeader";
import { Constants, TelemetryConstants } from "TFS/Views/ProjectOverviewPage/Constants";
import * as ProjectOverviewPageResources from "TFS/Views/ProjectOverviewPage/Resources";
import { StoreLoadingState } from "TFS/Views/ProjectOverviewPage/Shared/Contracts";
import { CurrentUserPermission, ProjectBasicData } from "TFS/Views/ProjectOverviewPage/Shared/Models";
import { ProjectMembersActionCreator } from "TFS/Views/ProjectOverviewPage/Shared/ProjectMembersActionCreator";
import { StoresHub } from "TFS/Views/ProjectOverviewPage/Stores/StoresHub";
import { ContributedPivotBarItemProvider } from "VSS/Features/Frame/ContributedPivotBarItemProvider";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Hub } from "VSSUI/Hub";
import { HubHeader } from "VSSUI/HubHeader";
import { IVssHubViewState, VssHubViewState } from "VSSUI/Navigation/VssHubViewState";
import { IVssPivotBarItemProvider } from "VSSUI/PivotBar";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

export interface ProjectOverviewPageProps {
    storesHub: StoresHub;
    projectOverviewActionCreator: ProjectOverviewPageActionCreator;
    projectMembersActionCreator: ProjectMembersActionCreator;
}

export interface ProjectOverviewPageState {
    showAddUsersDialog: boolean;
    showFavorites: boolean;
    userDialogLoading: boolean;
    loadingState: StoreLoadingState;
    projectBasicInfo: ProjectBasicData | undefined;
    userPermissions: CurrentUserPermission;
    isHosted: boolean;
}

export class ProjectOverviewPage extends VssComponent<ProjectOverviewPageProps, ProjectOverviewPageState> {
    private _hubViewState: IVssHubViewState;
    private _pivotProviders: IVssPivotBarItemProvider[];
    private _projectImageUrl?: string;

    constructor(props: ProjectOverviewPageProps, context: IVssComponentContext) {
        super(props, context);

        const { currentUserInfoStore, projectBasicInfoStore, projectMembersStore } = this.props.storesHub;
        const projectBasicInfoState = projectBasicInfoStore.getState();

        const tfsPageService = context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            this._projectImageUrl = tfsPageData.project.imageUrl;
        }

        this._hubViewState = new VssHubViewState(context.pageContext, {
            defaultPivot: Constants.ProjectOverviewTabKey
        });

        this._pivotProviders = [
            new ContributedPivotBarItemProvider({
                pageContext: context.pageContext,
                tabGroupIds: ["ms.vss-tfs-web.project-overview-tabs"],
                props: {
                    currentUserInfoStore: currentUserInfoStore,
                    projectBasicInfoStore: projectBasicInfoStore,
                    projectMembersActionCreator: props.projectMembersActionCreator,
                    projectMembersStore: projectMembersStore
                }
            })
        ];

        const pageData = context.pageContext.getService<IVssPageService>("IVssPageService").getData();
        const favoritesService = context.pageContext.getService<IFavoritesService>("IFavoritesService");

        this.state = {
            showAddUsersDialog: false,
            userDialogLoading: false,
            showFavorites: favoritesService.canUseFavorites(),
            loadingState: this._getLoadingState(),
            projectBasicInfo: projectBasicInfoState.projectBasicInfo,
            userPermissions: currentUserInfoStore.getState().currentUserPermission,
            isHosted: pageData ? pageData.isHosted : false
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.props.storesHub.currentUserInfoStore.addChangedListener(this._onCurrentUserInfoStoreChange);
        this.props.storesHub.projectBasicInfoStore.addChangedListener(this._onProjectInfoStoreChange);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.currentUserInfoStore.removeChangedListener(this._onCurrentUserInfoStoreChange);
        this.props.storesHub.projectBasicInfoStore.removeChangedListener(this._onProjectInfoStoreChange);
        super.componentWillUnmount();
    }

    public render(): JSX.Element | undefined {
        if (this.state.loadingState === StoreLoadingState.Loading) {
            return <Spinner />;
        } else if (this.state.loadingState === StoreLoadingState.Loaded) {
            const projectBasicInfo = this.state.projectBasicInfo!;
            const addUserDialogProps: AddUserDialogProps = {
                onAddUserDialogLoadStarted: this._onAddUserDialogLoadStarted,
                onAddUserDialogLoadCompleted: this._onAddUserDialogLoadCompleted,
                onAddUsersActionComplete: this._onAddUsersActionComplete,
                onAddUserDialogDismiss: this._onAddUserDialogDismiss,
                showAddUsersDialog: this.state.showAddUsersDialog
            };

            return (
                <Fabric className="project-overview-page">
                    <Hub pivotBarContentAriaLabel={ProjectOverviewPageResources.ProjectOverviewContentAriaLabel} className={"po-hub"} hubViewState={this._hubViewState} pivotProviders={this._pivotProviders} hideFullScreenToggle={true}>
                        <HubHeader>
                            <ProjectOverviewPageHeader
                                className={"po-header flex flex-grow scroll-hidden"}
                                projectBasicData={projectBasicInfo}
                                showInviteButton={this.state.userPermissions.isAdmin}
                                addUserDialogProps={addUserDialogProps}
                                onInviteButtonClick={this._onInviteButtonClick}
                                showFavorites={this.state.showFavorites}
                                isHosted={this.state.isHosted}
                                onEditNameClick={this._onEditNameClick}
                                projectImageUrl={this._projectImageUrl}
                                userPermissions={this.state.userPermissions}
                            />
                        </HubHeader>
                    </Hub>
                    {this.showOverlay && (
                        <Overlay className="project-overview-overlay flex flex-center justify-center" isDarkThemed={true}>
                            <Spinner size={SpinnerSize.large} label={ProjectOverviewPageResources.Loading_Label} ariaLive="assertive" />
                        </Overlay>
                    )}
                </Fabric>
            );
        }

        return undefined;
    }

    private _onProjectInfoStoreChange = (): void => {
        const { projectBasicInfoStore } = this.props.storesHub;
        const projectInfoState = projectBasicInfoStore.getState();

        this.setState({
            loadingState: this._getLoadingState(),
            projectBasicInfo: projectInfoState.projectBasicInfo
        });
    };

    private _onCurrentUserInfoStoreChange = (): void => {
        const { currentUserInfoStore } = this.props.storesHub;
        const currentUserInfoState = currentUserInfoStore.getState();

        this.setState({
            loadingState: this._getLoadingState(),
            userPermissions: currentUserInfoState.currentUserPermission
        });
    };

    private _getLoadingState = (): StoreLoadingState => {
        const { currentUserInfoStore, projectBasicInfoStore } = this.props.storesHub;
        const currentUserInfoState = currentUserInfoStore.getState();
        const projectInfoState = projectBasicInfoStore.getState();

        if (currentUserInfoState.loadingState === StoreLoadingState.Loading || projectInfoState.loadingState === StoreLoadingState.Loading) {
            return StoreLoadingState.Loading;
        } else if (currentUserInfoState.loadingState === StoreLoadingState.Loaded && projectInfoState.loadingState === StoreLoadingState.Loaded) {
            return StoreLoadingState.Loaded;
        }

        return StoreLoadingState.LoadFailed;
    };

    private _onAddUserDialogLoadStarted = (): void => {
        this.setState({ userDialogLoading: true });
    };

    private _onAddUserDialogLoadCompleted = (): void => {
        this.setState({ userDialogLoading: false });
    };

    private _onAddUserDialogDismiss = (): void => {
        this.setState({ showAddUsersDialog: false });
    };

    private get showOverlay() {
        return this.state.showAddUsersDialog && this.state.userDialogLoading;
    }

    private _onAddUsersActionComplete = (): void => {
        this.props.projectMembersActionCreator.fetchProjectMembers(true);
        this.setState({ showAddUsersDialog: false });
    };

    private _onInviteButtonClick = (): void => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");

        telemetryService.publishEvent(Constants.Area, TelemetryConstants.InviteButtonClick, {
            [TelemetryConstants.ProjectId]: this.state.projectBasicInfo ? this.state.projectBasicInfo.id : undefined
        });

        if (this.state.isHosted) {
            this.setState({ showAddUsersDialog: true });
        } else {
            const legacyService = this.context.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");

            legacyService.getLegacyPageContext(true).then((legacyPageContext: any) => {
                legacyService
                    .requireModules(["Admin/Scripts/TFS.Admin.Registration.HostPlugins", "VSS/Events/Action"])
                    .then(([dialogModule, events_Action]: any) => {
                        events_Action.getService().performAction("manage-team-members", {
                            teamName: legacyPageContext.webContext.project.name,
                            teamId: legacyPageContext.webContext.team.id,
                            callback: (membersModified: boolean) => {
                                if (membersModified) {
                                    this.props.projectMembersActionCreator.fetchProjectMembers(true);
                                }
                            }
                        });
                    });
            });
        }
    };

    private _onEditNameClick = (): void => {
        const projectName = this.state.projectBasicInfo!.name;
        const editNameLink = routeUrl(this.context.pageContext, "ms.vss-admin-web.project-admin-hub-route", { project: projectName });
        window.location.href = editNameLink;

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(Constants.Area, TelemetryConstants.EditNameButtonClick, {
            [TelemetryConstants.ProjectId]: this.state.projectBasicInfo ? this.state.projectBasicInfo.id : undefined
        });
    };
}
